export type PropType =
  | "boolean"
  | "string"
  | "number"
  | "enum"
  | "function"
  | "ReactNode"
  | "object"
  | "array"
  | string;

export type PlatformSplit<T> = { react?: T; rails?: T };

export interface PropDefinition {
  type: PropType | PlatformSplit<PropType>;
  platforms: ("react" | "rails")[];
  values?: string[];
  default?: any;
  responsive?: boolean;
  description?: string;
  example?: string;
  /** When true, an empty string value is emitted as `prop=""` in generated code instead of being omitted. */
  emitEmptyString?: boolean;
}

export interface KitSchema {
  $schema: string;
  name: string;
  description: string;
  platforms: ("react" | "rails")[];
  props: Record<string, PropDefinition>;
  globalProps: boolean;
  usage: {
    react?: {
      import: string;
      example: string;
    };
    rails?: {
      import: string | null;
      example: string;
    };
  };
}

export interface GlobalPropsSchema {
  $schema: string;
  name: string;
  description: string;
  breakpoints: Record<string, string>;
  spacing: {
    values: string[];
    description: string;
    tokens: Record<string, string>;
  };
  props: Record<string, PropDefinition>;
}

export interface PropValue {
  value: any;
  enabled: boolean;
}

export interface PlaygroundState {
  propValues: Record<string, PropValue>;
  children: string;
}

export interface PropControlProps {
  name: string;
  definition: PropDefinition;
  value: PropValue | undefined;
  onChange: (name: string, value: PropValue) => void;
}

export type FunctionPreset = {
  label: string;
  value: string;
  code: string;
};

export const FUNCTION_PRESETS: FunctionPreset[] = [
  { label: "None", value: "", code: "" },
  { label: "Alert", value: "alert", code: "() => alert('Clicked!')" },
  { label: "Console Log", value: "console", code: "() => console.log('Clicked!')" },
  { label: "Custom Handler", value: "custom", code: "(e) => { /* your code */ }" },
];

export type ReactNodePreset = {
  label: string;
  value: string;
};

export const REACT_NODE_PRESETS: ReactNodePreset[] = [
  { label: "Text", value: "text" },
  { label: "Icon", value: "icon" },
  { label: "Custom JSX", value: "custom" },
];

export interface PlaygroundChildrenPropInjection {
  component: string;
  propTarget: string;
  /** When set, injection runs only in these structure mode keys (e.g. `"standard"`). */
  structureModes?: string[];
}

export interface PlaygroundChildrenConfig {
  editable: boolean;
  default: string;
  marker?: string;
  hideWhenPropSet?: string[];
  propInjection?: PlaygroundChildrenPropInjection;
}

export interface PropCondition {
  requires?: string | Record<string, any>;
  showWhen?: string | Record<string, any>;
  /** When set, the control is enabled only in this structure mode (e.g. `"explicit"`). */
  structureMode?: string;
}

export interface PropGroup {
  name: string;
  props: string[];
  collapsed?: boolean;
}

export interface PlaygroundPreset {
  name: string;
  /** When omitted, preset applies no feature props (dataPreset / structureMode may still apply). */
  props?: Record<string, any>;
  children?: string;
  /** When set, applying this preset selects this `structureModes.modes` key (e.g. `"explicit"` for subcomponent templates). */
  structureMode?: string;
  /** Key in `dataPresets.presets` — loads that bundle’s `columnDefinitions` + `tableData`. */
  dataPreset?: string | null;
}

export interface PlaygroundHint {
  /** Prop-based conditions (effective values). Omit or use `{}` for no prop checks. */
  when?: Record<string, any>;
  message: string;
  type?: "info" | "warning" | "error";
  /** Show only when the feature preset at this index is active (0-based `presets` array). */
  presetIndex?: number;
  /** Show only when a preset with this `name` is active (stable if presets reorder). */
  presetName?: string;
}

export interface StructureMode {
  label: string;
  template: string;
  children: string;
  props?: Record<string, any>;
  propTargets?: Record<string, string>;
  propAliases?: Record<string, string>;
  imports?: string[];
  externalImports?: string[];
  wrapper?: string;
  statefulProps?: string[];
}

export interface StructureModesConfig {
  default: string;
  modes: Record<string, StructureMode>;
}

/** Named bundles of `columnDefinitions` + `tableData` for kits that need different sample data (e.g. Advanced Table). */
export interface DataPreset {
  label: string;
  columnDefinitions: any[];
  tableData: any[];
}

export interface DataPresetsConfig {
  presets: Record<string, DataPreset>;
}

/** When a prop is turned on, optionally select matching sample data and/or structure mode (kits opt in). */
export interface PropSyncOnEnableRule {
  /** Key in `dataPresets.presets` */
  dataPreset?: string;
  /** Key in `structureModes.modes` */
  structureMode?: string;
}

export type PropSyncOnEnable = Record<string, PropSyncOnEnableRule>;

export interface PlaygroundConfig {
  template: string;
  propTargets?: Record<string, string>;
  propAliases?: Record<string, string>;
  customProps?: Record<string, PropDefinition>;
  defaults?: Record<string, any>;
  /** Component runtime defaults to use when deciding whether generated JSX may omit a prop. */
  codegenDefaultProps?: Record<string, any>;
  scopeVars?: Record<string, any>;
  children?: PlaygroundChildrenConfig;
  conditionals?: Record<string, PropCondition>;
  groups?: PropGroup[];
  presets?: PlaygroundPreset[];
  hints?: Record<string, PlaygroundHint>;
  structureModes?: StructureModesConfig;
  wrapper?: string;
  /** Swap required table/column data without duplicating feature presets for each dataset. */
  dataPresets?: DataPresetsConfig;
  /** When a control is enabled, co-select sample data and/or structure mode (see Advanced Table). */
  propSyncOnEnable?: PropSyncOnEnable;
  requiredProps?: Record<string, any>;
  /** Default/state props that must be emitted in copied code, but do not need template variables. */
  requiredCodeProps?: string[];
  /** Props consumed by the template/wrapper as variables instead of emitted through `{{props}}`. */
  statefulProps?: string[];
  /** Kit prop names to omit from the playground props panel (still in kit.schema.json). */
  hiddenProps?: string[];
  externalImports?: string[];
  /** Prop names for which an explicit empty string is a meaningful value and should be emitted as `prop=""` instead of omitted. */
  emitEmptyStringProps?: string[];
  /** Function props that should use an editable code field instead of handler presets. */
  editableFunctionProps?: string[];
}
